/**
 * Single-flight refresh.
 *
 * When an access token expires, every in-flight request 401s at roughly the same
 * moment. Refreshing per request would fire N rotations against a token family
 * that is single-use by design — the API's reuse detector would read the second
 * one as theft and revoke every session. So exactly ONE rotation is ever in
 * flight; everyone else awaits the same promise and retries with its result.
 *
 * Deliberately uses bare `fetch`, not `apiRequest`: the client's 401 handler
 * calls back into here, and that loop must not exist.
 */

import type { AuthTokens } from '@cyphex/types';
/**
 * Reaches INTO a feature, which nothing else in `lib/` does — deliberately, and
 * only for these two pure helpers. The rule this protects is an auth rule, so it
 * belongs on the one path that can break it: a call window must not rotate the
 * shared, single-use refresh token out from under the window that opened it.
 * The alternative was to duplicate the "am I a call window" test and the
 * message names here, and auth logic that lives in two files drifts.
 */
import { isDesktopCallWindow, requestShellToken } from '@/features/desktop/callWindow';
import { getWorkspaceSlug, readCsrfToken } from './authStorage';
import { API_BASE_URL } from './env';
import { onAccessTokenChange, setAccessToken } from './token';

let inFlight: Promise<string | null> | null = null;

/*
 * "NO SESSION" IS AN ANSWER, AND IT IS REMEMBERED FOR A MOMENT.
 *
 * Single-flight only collapses refreshes that overlap. A signed-out page asks
 * again on every request it makes - the realtime handshake, every query, the
 * shell bridge - and each one was a fresh `POST /auth/refresh` that could only
 * 401. On 25 Sep a signed-out desktop shell sent twenty in one minute, and the
 * sign-in the person was typing shared that minute's budget. Nothing can change
 * the answer until somebody signs in, and that lands as a token, which clears
 * this at once; a sign-in in ANOTHER tab is picked up when the quiet ends.
 */
const NO_SESSION_QUIET_MS = 15_000;
let noSessionAt = 0;
onAccessTokenChange((token) => {
  if (token !== null) noSessionAt = 0;
});

/** Resolves to a fresh access token, or `null` when the session is really gone. */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight === null) {
    const attempt = rotate();
    inFlight = attempt;
    void attempt.finally(() => {
      if (inFlight === attempt) inFlight = null;
    });
  }
  return inFlight;
}

async function rotate(): Promise<string | null> {
  // ══════════════════════════════════════════════════════════════════════════
  // A DESKTOP CALL WINDOW MUST NEVER REACH `POST /auth/refresh`. NOT EVER.
  //
  // The desktop shell puts a live call in its own BrowserWindow, and every
  // window in that shell runs in `session.defaultSession` — one cookie jar, one
  // `cxt_rt`, which is SINGLE-USE. A rotation from the call window makes the
  // API revoke the workspace window's session and tombstone it in Redis, so the
  // workspace starts 401ing on its very next request; it then refreshes too,
  // and if it presents the value it read before the new `Set-Cookie` landed the
  // API reads a replay of a rotated token, assumes theft, and burns the entire
  // family. Both windows land on the sign-in page. Clicking "call" signed you
  // out twice over — this is not hypothetical, it is what shipped and was
  // reverted, and the comment that used to sit here described the revert.
  //
  // THIS IS THE RIGHT SEAM AND THE ONLY ONE. Four callers funnel through here —
  // `AuthProvider`'s cold start, `apiRequest`'s no-token and 401 branches, and
  // the gateway handshake in `createRealtimeClient` — and `AuthProvider` does
  // NOT check whether a token is already held before rotating, so seeding the
  // token store on the way in would not have stopped it.
  //
  // NO FALLBACK. Not "prefer the shell and refresh if it times out": a fallback
  // here is the bug. When the broker cannot answer, the honest result is no new
  // token, and the previous one is left in place so a call in progress survives
  // a blip rather than being signed out by one. See main/authBroker.ts.
  // ══════════════════════════════════════════════════════════════════════════
  if (isDesktopCallWindow()) {
    const brokered = await requestShellToken(true);
    if (brokered !== null) setAccessToken(brokered);
    return brokered;
  }

  if (noSessionAt !== 0 && Date.now() - noSessionAt < NO_SESSION_QUIET_MS) return null;

  const csrfToken = readCsrfToken();
  const workspace = getWorkspaceSlug();

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
        ...(csrfToken === null ? {} : { 'X-CSRF-Token': csrfToken }),
      },
      // The refresh token itself rides the httpOnly cookie. The body carries
      // only the workspace hint the API needs to pick a tenant context.
      body: JSON.stringify(workspace === null ? {} : { tenantSlug: workspace }),
    });
  } catch {
    // Offline. Keep whatever token we hold and let the caller surface the
    // error — dropping the session on a flaky network signs people out for
    // walking into a lift.
    return null;
  }

  if (!response.ok) {
    // ════════════════════════════════════════════════════════════════════════
    // ONLY THE API SAYING "NO" ENDS A SESSION. A BAD MINUTE DOES NOT.
    //
    // This cleared the token on ANY non-2xx, which reads a transport failure as
    // a verdict. `POST /auth/refresh` is rate limited (`rateLimit('auth', 120)`
    // in apps/api/src/routes/auth/login.ts), so a burst across tabs and call
    // windows can take a 429; nginx answers 502/503 through an API restart or a
    // rolling deploy; the API itself can 500. None of those means the session is
    // over, and every one of them is ORDINARY on a self-hosted product where the
    // owner restarts their own server.
    //
    // Clearing on them is not merely a needless sign-out. On the desktop the
    // clear is a genuine TRANSITION, so it is published to the shell as one
    // (`useDesktopAuthBridge`), pushed to every live call window, and each of
    // them takes itself away MID-CALL — without even hanging up first, because
    // by then it has no token to hang up with. A flaky network must not end a
    // call: the branch above already refuses to let a request that never left
    // the machine do it, and this is the same rule for one that left and came
    // back as a five.
    //
    // 401 and 403 ARE the verdict — an expired, revoked, replayed or missing
    // refresh token, or a CSRF check that failed. Those, and only those, end it.
    // Anything else keeps the token we hold and lets the caller surface the
    // error, exactly like being offline.
    // ════════════════════════════════════════════════════════════════════════
    if (response.status === 401 || response.status === 403) {
      noSessionAt = Date.now();
      setAccessToken(null);
    }
    return null;
  }

  const payload = (await response.json().catch(() => null)) as { tokens?: AuthTokens } | null;
  const token = payload?.tokens?.accessToken ?? null;
  setAccessToken(token);
  return token;
}
